import { Box } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { RadioBoxButton } from '@/components';
import {
  onboardingActions,
  selectOnboardingStepInputs,
  selectPlan,
  STEP_IDS,
  useAppDispatch,
  useAppSelector,
  useFetchUserProfileQuery,
} from '@/store';

import { styles } from '../styles';
import { Footer } from './Footer';
import { Header } from './Header';
import { ModalSubtitle } from './ModalSubtitle';
import { ModalTitle } from './ModalTitle';

export const Introduction = () => {
  const dispatch = useAppDispatch();
  const stepInputs = useAppSelector(selectOnboardingStepInputs);
  const { value: isTeamUse } = stepInputs[STEP_IDS.introduction];
  const { data: user } = useFetchUserProfileQuery();
  const displayName = user?.firstName;
  const { search } = useLocation();
  const { data: plan } = useAppSelector(selectPlan);
  const inviteToken = new URLSearchParams(search).get('inviteToken');
  const isInvitedUser = !!inviteToken || !!plan;

  const updateInput = useCallback(
    (input: boolean) => {
      dispatch(
        onboardingActions.updateStepInputs({
          step: STEP_IDS.introduction,
          input,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (isInvitedUser) {
      updateInput(false);
    }
  }, [isInvitedUser, updateInput]);

  return (
    <Box sx={styles.formContainer}>
      {isInvitedUser ? (
        <>
          <Header
            Title={<Title userName={displayName} />}
            Subtitle={<Subtitle userName={displayName} />}
          />
          <Box sx={styles.formPrimarySection} />
          <Footer />
        </>
      ) : (
        <>
          <Header
            Title={
              <ModalTitle>Welcome! How do you plan on using LumiQ?</ModalTitle>
            }
            Subtitle={
              <ModalSubtitle>
                Tell us a bit about yourself to help customize your experience.
              </ModalSubtitle>
            }
          />
          <Box sx={styles.formPrimarySection}>
            <Box sx={styles.checkboxContainer}>
              <RadioBoxButton
                label={'For myself'}
                checked={isTeamUse === false}
                onChange={() => updateInput(false)}
              />
            </Box>
            <Box sx={styles.checkboxContainer}>
              <RadioBoxButton
                label={'With my team'}
                checked={isTeamUse === true}
                onChange={() => updateInput(true)}
              />
            </Box>
          </Box>
          <Footer />
        </>
      )}
    </Box>
  );
};

const Title = ({ userName }: { userName?: string }) =>
  userName ? (
    <ModalTitle>Hi{userName && ` ${userName}`},</ModalTitle>
  ) : (
    <ModalTitle>Welcome to LumiQ</ModalTitle>
  );

const Subtitle = ({ userName }: { userName?: string }) =>
  userName ? (
    <ModalSubtitle>
      We need some information about you in order to ensure compliance with
      regulatory requirements while also offering you the most tailored content.
      Please answer a few short questions about yourself (it will take 60
      seconds).
    </ModalSubtitle>
  ) : (
    <ModalSubtitle>
      Please answer a few questions about yourself to serve up relevant content
      and tailor your learning experience, it will take 60 seconds.
    </ModalSubtitle>
  );
